import React, {useState} from "react";
import Container from "../layout/Container";
import {useTheme} from "../../theme/ThemeProvider";
import {buildStyles} from "../buildStyles";
import PropTypes from "prop-types";

const useStyles = (theme) =>
  buildStyles({
    switchContainer: {
      position: "relative",
      width: "44px",
      height: "24px",
      borderRadius: `${theme.radius.xxl}px`,
      backgroundColor: theme.colors.gray200,
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    switchContainerActive: {
      backgroundColor: theme.colors.primary500,
    },
    knob: {
      position: "absolute",
      top: "2px",
      left: "2px",
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      backgroundColor: theme.colors.white,
      boxShadow: theme.shadows.sm,
      transition: "left 0.2s ease",
    },
    knobActive: {
      left: "22px",
    },
  });

/**
 * Switch is a component that renders a toggle switch allowing the user to switch between two states.
 * It visually indicates whether it is on or off and supports state changes through the `onChange` callback.
 *
 * @param {Object} props - The props for the Switch component.
 * @param {boolean} [props.isChecked=false] - The initial checked state of the switch.
 * @param {Function} [props.onChange] - The function to call when the switch state changes.
 *
 * @returns {JSX.Element} The rendered Switch component.
 */
const Switch = ({isChecked = false, onChange}) => {
  const [checked, setChecked] = useState(isChecked);
  const {theme} = useTheme();
  const classes = useStyles(theme);

  const handleToggle = () => {
    const newCheckedState = !checked;
    setChecked(newCheckedState);
    onChange && onChange(newCheckedState);
  };

  return (
    <Container
      as="div"
      className={`${classes.switchContainer} ${
        checked ? classes.switchContainerActive : ""
      }`}
      onClick={handleToggle}
    >
      <Container
        as="div"
        className={`${classes.knob} ${checked ? classes.knobActive : ""}`}
      />
    </Container>
  );
};

Switch.propTypes = {
  isChecked: PropTypes.bool,
  onChange: PropTypes.func,
};

export default Switch;
